import { STATUS } from '@/constants';
import type { Meta, Profile, Status } from '@/types';
import { PL_LOG_GROUPS, logger, resolveAuthState } from '@/utils';
import { LOG_LEVEL, STAGE, getServiceRegionConfig, tryCatch } from '@b2x/shared';

type ResponseStatus = Extract<Status, 'SUCCESS' | 'FAILURE'>;

type ProfilesResponse = {
  profiles: Profile[];
  meta: Meta<ResponseStatus>;
};

export async function fetchProfiles(): Promise<ProfilesResponse> {
  const { serviceUrl, country } = await getServiceRegionConfig('profile-bff', [STAGE.LOCAL, STAGE.TEST, STAGE.PPROD]);

  const { jwe } = await resolveAuthState();

  const headers = {
    ...(jwe && { Authorization: `Bearer ${jwe}` }),
    'x-tracking-id': crypto.randomUUID(),
  };

  const { data, error } = await tryCatch<ProfilesResponse>(
    (async () =>
      (
        await fetch(`${serviceUrl}/profile/${country}`, {
          method: 'GET',
          headers,
        })
      ).json())(),
  );

  if (error) {
    const response = {
      profiles: [],
      meta: { status: STATUS.FAILURE, message: 'Error fetching profiles' },
    };
    logger('Error fetching profiles', {
      args: [error.message, response],
      group: PL_LOG_GROUPS.CORE,
      level: LOG_LEVEL.WARN,
    });

    return response;
  }

  const response = {
    profiles: data.profiles,
    meta: data.meta,
  };

  logger('Fetched profiles', {
    args: [response],
    group: PL_LOG_GROUPS.CORE,
    level: LOG_LEVEL.INFO,
  });

  return response;
}
